<template>
  <div class="pt-8 flex flex-col lg:flex-row p-4 min-h-screen bg-light-bg dark:bg-dark-bg dark:text-dark-text">
    <AppSidebar />

    <div class="flex flex-col flex-grow p-6 lg:p-12 space-y-4 h-full">
      <div v-if="message" :class="messageType" 
          class="fixed top-0 left-0 right-0 z-5 p-4 rounded-lg mb-4 text-center 
                  bg-white dark:bg-dark-box-bg dark:text-dark-text dark:border 
                  dark:border-dark-border">
          {{ message }}
          <div class="absolute bottom-0 left-0 h-1 rounded-lg" 
              :style="{ width: progress + '%', backgroundColor: getProgressColor(), borderRadius: '8px' }">
          </div>
      </div>

      <h1 class="text-xl md:text-2xl font-bold mb-4 text-center lg:text-left">Archiwum Firm</h1>

      <div class="bg-white p-4 rounded-lg shadow-md w-full h-auto overflow-visible dark:bg-dark-box-bg dark:border dark:border-dark-border dark:text-dark-text">
        <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 space-y-4 sm:space-y-0">
          <h2 class="text-lg font-semibold text-gray-700 dark:text-dark-text">Lista Archiwalnych Firm:</h2>

          <!-- Wyszukiwanie -->
          <DynamicSearch
              :data="companies"
              :keysToSearch="['companyName', 'companyNip', 'companyAddress', 'phone']"
              @search-results="handleSearchResults"
              @clear-search="clearSearch"
              class="mt-4 bg-gray-200 dark:bg-dark-box-bg text-gray-900 dark:text-dark-text w-full md:w-auto"
              placeholder="Wyszukaj..."
          />
        </div>

        <div class="overflow-x-auto w-full">
          <table class="min-w-full bg-white border border-gray-300 rounded-lg dark:bg-dark-table-bg dark:border-dark-border">
            <thead>
            <tr class="bg-gray-50 dark:bg-dark-table-header dark:text-dark-text">
              <th class="py-3 px-4 border-b border-gray-300 text-left text-sm font-semibold text-gray-700 dark:border-dark-border dark:text-dark-text">Nazwa Firmy</th>
              <th class="py-3 px-4 border-b border-gray-300 text-left text-sm font-semibold text-gray-700 dark:border-dark-border dark:text-dark-text">NIP Firmy</th>
              <th class="py-3 px-4 border-b border-gray-300 text-left text-sm font-semibold text-gray-700 dark:border-dark-border dark:text-dark-text"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-if="filteredCompanies.length === 0">
              <td colspan="6" class="text-center py-4 text-red-500 font-semibold dark:text-dark-status-unavailable">Brak archiwalnych firm</td>
            </tr>
            <tr 
              v-else 
              v-for="(company) in paginatedCompanies" 
              :key="company.id" 
              class="border-b border-gray-300 dark:border-dark-border hover:bg-light-hover dark:hover:bg-dark-hover hover:cursor-pointer"
            >
              <td @click="showCompanyDetails(company)" class="py-3 px-4 dark:text-dark-text">{{ company.companyName }}</td>
              <td class="py-3 px-4 dark:text-dark-text">{{ company.companyNip }}</td>
              <td class="py-3 px-4 text-right dark:text-dark-text">
                <div class="relative inline-block text-left">
                  <button @click="toggleDropdown(company.id)" class="text-gray-500 hover:text-gray-700 dark:text-dark-text dark:hover:text-gray-400">
                    <EllipsisVerticalIcon class="h-5 w-5" />
                  </button>
                  <div v-if="dropdownOpen === company.id" class="origin-top-right absolute left-0 ml-[-10rem] mt-[-2.2rem] w-40 focus:outline-none">
                    <div class="py-1.5 absolute flex space-x-2 bg-gray-200 dark:bg-gray-900 rounded">
                      <button @click="goToProfile(company)" class="text-left px-2 py-2 text-sm text-teal-700 dark:text-teal-400 flex items-center space-x-2">
                        <DocumentIcon class="w-4 h-4" />
                      </button>
                      <button @click="restoreCompany(company.id)" class="text-left px-2 py-2 text-sm text-teal-700 dark:text-teal-400 flex items-center space-x-2">
                        <PlusIcon class="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <div class="mt-4 flex items-center justify-between">
          <div class="text-sm text-light-text dark:text-gray-400">
            Wyświetlono {{ (currentPage - 1) * itemsPerPage + paginatedCompanies.length }} z {{ paginatedCompanies.length }} archiwalnych firm
          </div>

          <!-- Paginacja -->
          <div class="flex flex-wrap justify-center sm:justify-end items-center gap-2">
            <!-- Pierwsza strona -->
            <button v-if="currentPage > 1" @click="goToPage(1)" class="px-3 py-1 bg-light-table-header text-sm font-medium rounded-md dark:bg-dark-table-header dark:text-dark-text">
              Pierwsza strona
            </button>
            <!-- Poprzednia strona -->
            <button v-if="currentPage > 1" @click="prevPage" class="px-3 py-1 bg-light-table-header text-sm font-medium rounded-md dark:bg-dark-table-header dark:text-dark-text">
              &laquo; Poprzednia
            </button>
            <!-- Widoczne strony -->
            <button v-for="page in visiblePages" :key="page" @click="goToPage(page)"
                    :class="[currentPage === page ? 'bg-teal-700 text-white' : 'bg-gray-200 dark:bg-dark-box-bg text-gray-700 dark:text-dark-text']"
                    class="px-3 py-1 rounded-lg text-sm cursor-pointer">
              {{ page }}
            </button>
            <!-- Następna strona -->
            <button v-if="currentPage < totalPages" @click="nextPage" class="px-3 py-1 bg-light-table-header text-sm font-medium rounded-md dark:bg-dark-table-header dark:text-dark-text">
              Następna &raquo;
            </button>
            <!-- Ostatnia strona -->
            <button v-if="currentPage < totalPages" @click="goToPage(totalPages)" class="px-3 py-1 bg-light-table-header text-sm font-medium rounded-md dark:bg-dark-table-header dark:text-dark-text">
              Ostatnia strona
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>



<script>
  import { useAuthStore } from '@/stores/authStore';
  import AppSidebar from '@/components/AppSidebar.vue';
  import DynamicSearch from '../search/DynamicSearch.vue';
  import { EllipsisVerticalIcon, DocumentIcon, PlusIcon } from "@heroicons/vue/24/solid";
import companiesService from '@/services/companiesService';

  export default {
    name: "CompaniesArchivePageAdmin",
    components: {
      EllipsisVerticalIcon,
      DocumentIcon,
      PlusIcon,
      AppSidebar,
      DynamicSearch
    },
    computed: {
      paginatedCompanies() {
        const start = (this.currentPage - 1) * this.itemsPerPage;
        const end = start + this.itemsPerPage;
        return this.filteredCompanies.slice(start, end);
      }
    },
    setup() {
      const authStore = useAuthStore();
      if (authStore.isAuthenticated) {
        authStore.checkExpiration();
      }
      return { user: authStore.user };
    },
    data() {
        return {
            filteredCompanies: [],
            companies: [],
            dropdownOpen: null,
            message: '',
            messageType: '',
            progress: 0,
            loading: false,
            isSearching: false,

            currentPage: 1,
            itemsPerPage: 15,
            totalPages: 0,
            visiblePages: []
          };
    },
    mounted() {
      this.calculateItemsPerPage();
      window.addEventListener("resize", this.calculateItemsPerPage);

      const pageFromUrl = parseInt(this.$route.query.currentPage) || 1;
      this.currentPage = pageFromUrl;

      this.fetchArchiveCompany().then(() => {
        this.calculateTotalPages(); // Oblicz paginację
      });
    },
    beforeUnmount() {
      window.removeEventListener("resize", this.calculateItemsPerPage);
    },
    methods: {
      calculateTotalPages() {
        this.totalPages = Math.ceil(this.filteredCompanies.length / this.itemsPerPage) || 1;
        this.updateVisiblePages();
      },
      updateVisiblePages() {
        const range = 5; // Liczba stron widocznych na raz
        const start = Math.max(1, this.currentPage - Math.floor(range / 2));
        const end = Math.min(start + range - 1, this.totalPages);

        this.visiblePages = Array.from({ length: end - start + 1 }, (_, i) => start + i);
      },
      goToPage(page) {
        this.currentPage = page;
        this.updateVisiblePages();
      },
      nextPage() {
        if (this.currentPage < this.totalPages) {
          this.currentPage++;
          this.updateVisiblePages();
        }
      },
      prevPage() {
        if (this.currentPage > 1) {
          this.currentPage--;
          this.updateVisiblePages();
        }
      },
      calculateItemsPerPage() {
        const width = window.innerWidth;

        if (width >= 3840) {
          this.itemsPerPage = 24; // 4K monitor
        } else if (width >= 2560) {
          this.itemsPerPage = 15; // 2K monitor
        } else if (width >= 1920) {
          this.itemsPerPage = 8; // Full HD monitor
        } else {
          this.itemsPerPage = 5; // Standardowa wartość
        }

        this.calculateTotalPages();
      },
      showMessage(msg, type) {
        this.message = msg;
        this.messageType = type === 'success' ? 'success-message' : 'error-message';
        this.progress = 0;

        const interval = 30;
        const duration = 5000;
        const step = 100 / (duration / interval);

        const progressInterval = setInterval(() => {
          this.progress += step;
          if (this.progress >= 100) {
            clearInterval(progressInterval);
            this.progress = 0;
            this.message = '';
            this.messageType = '';
          }
        }, interval);
      },
      getProgressColor() {
        return this.messageType === 'success-message' ? '#16a34a' : '#b91c1c';
      },
      async fetchArchiveCompany() {
        try {
          const response = await companiesService.getAllCompaniesArchive();
          this.companies = response.data;    
          this.filteredCompanies = this.companies;
        } catch (error) {
          console.error('Błąd podczas pobierania firm:', error);
        }
      },
      async restoreCompany(companyId) {
        if (confirm("Czy na pewno chcesz przywrócić tą firmę?")) {
          try {
            await companiesService.restoreCompany(companyId);
            await this.fetchArchiveCompany();
            this.showMessage("Firma została pomyślnie przywrócona.", "success");
          } catch (error) {
            console.error('Błąd podczas usuwania zamówienia:', error);
            this.showMessage("Wystąpił błąd podczas przywracania firmy.", "error");
          }
        }
      },
      goToProfile(company) {
        if (this.user.role === 'ROLE_ADMIN') {
          this.$router.push({
            name: 'CompanyProfileAdmin', 
            params: {id: company.id},
            query: { 
              returnTo: this.$route.name,
            }
          });
        } else {
          this.$router.push({
            name: 'CompanyProfileDispatcher', 
            params: {id: company.id},
            query: { 
              returnTo: this.$route.name,
            }
          });
        }
      },
      formatDate(dateString) {
        const date = new Date(dateString);

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${year}-${month}-${day} ${hours}:${minutes}`;
      },
      toggleDropdown(orderId) {
        this.dropdownOpen = this.dropdownOpen === orderId ? null : orderId;
      },
      handleSearchResults(filteredData) {
        this.filteredCompanies = filteredData;
        this.calculateTotalPages();

        this.isSearching = this.filteredCompanies.length > 0;
      },
      clearSearch() {
        this.isSearching = false;
      }
    }
  };
  </script>
  